<template>
    <div class="wizard-step">
        <b-row>
            <b-col cols="12">
                <h3>Agreement Details</h3>
                <p class="mt-4" v-html="config.introText"></p>
                <StepIndicator :current-step="currentStep" :expanded="true" :config="config" class="mt-4" @show-terms="$emit('show-terms')" />

                <b-card v-if="config.whatYoullNeed" class="what-youll-need mt-4">
                    <h5 class="mb-2">
                        <i class="fas fa-clipboard-list text-primary mr-2"></i>{{config.whatYoullNeed.title}}
                    </h5>
                    <p class="mb-2">{{config.whatYoullNeed.intro}}</p>
                    <ul class="what-youll-need-list mb-3">
                        <li v-for="(item, index) in config.whatYoullNeed.items" :key="index">{{item}}</li>
                    </ul>
                    <p v-if="config.whatYoullNeed.note" class="mb-0 no-credit-check">
                        <i class="fas fa-check-circle text-success mr-2"></i><strong>{{config.whatYoullNeed.note}}</strong>
                    </p>
                </b-card>
            </b-col>
        </b-row>

        <!-- Agreement Details Table -->
        <b-row class="mt-5">
            <b-col cols="12">
                <b-card class="details-card">
                    <table class="details-table">
                        <tr v-if="config.merchant">
                            <th class="no-border">Merchant</th>
                            <td class="no-border"><strong>{{config.merchant.name}}</strong></td>
                        </tr>
                        <tr v-if="config.merchant">
                            <th class="no-border"></th>
                            <td class="no-border">
                                <div><a :href="config.merchant.url" target="_blank">{{config.merchant.url}}</a> <i style="font-size: 0.7rem; color: #666" class="far fa-external-link ml-2"></i></div>
                                <div class="mt-1"><a :href="'mailto:' + config.merchant.email">{{config.merchant.email}}</a></div>
                                <div class="mt-1">{{config.merchant.phone}}</div>
                                <div class="mt-1">{{config.merchant.address}}<br>{{config.merchant.cityStateZip}}</div>
                            </td>
                        </tr>
                        <tr v-for="(field, index) in (config.detailFields || [])" :key="index">
                            <th>{{field.label}}</th>
                            <td>
                                <template v-if="field.value">{{field.value}}</template>
                                <template v-else-if="field.format === 'currency'">{{field.prefix || ''}}{{plan[field.key] | currency}}{{field.suffix || ''}}</template>
                                <template v-else-if="field.format === 'date'">{{field.prefix || ''}}{{plan[field.key] | moment('MMMM Do, YYYY')}}{{field.suffix || ''}}</template>
                                <template v-else>{{field.prefix || ''}}{{plan[field.key] || field.default || ''}}{{field.suffix || ''}}</template>
                            </td>
                        </tr>
                    </table>
                </b-card>
            </b-col>
        </b-row>

        <!-- Important Information Checkboxes -->
        <b-row class="mt-5">
            <b-col cols="12">
                <h5>Important Information</h5>
                <p class="mt-4 mb-4">{{config.termsCheckboxIntro}}</p>

                <div class="mb-5">
                    <b-button variant="primary" @click="onViewTerms">
                        <i class="fas fa-file-contract mr-2"></i>View Full Terms and Conditions
                    </b-button>
                </div>

                <div class="checkbox-list">
                    <div class="checkbox-item" v-for="(term, index) in (config.termsCheckboxes || [])" :key="index">
                        <b-form-checkbox v-model="termsAcknowledged[index]">
                            {{term}}
                        </b-form-checkbox>
                    </div>
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import StepIndicator from '@/components/sign-agreement/StepIndicator.vue';

export default {
    name: 'AgreementDetails',
    components: {
        StepIndicator
    },
    props: {
        plan: {
            type: Object,
            required: true
        },
        termsAcknowledged: {
            type: Array,
            required: true
        },
        currentStep: {
            type: Number,
            required: true
        },
        config: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            hasEmittedAllTerms: false
        };
    },
    computed: {
        allTermsAcknowledged() {
            return this.termsAcknowledged.every(term => term === true);
        }
    },
    watch: {
        termsAcknowledged: {
            handler(newVal, oldVal) {
                newVal.forEach((checked, index) => {
                    if (checked && (!oldVal || !oldVal[index])) {
                        this.$emit('audit-event', {
                            eventType: 'term_acknowledged',
                            data: {
                                termIndex: index
                            }
                        });
                    }
                });

                if (this.allTermsAcknowledged && !this.hasEmittedAllTerms) {
                    this.hasEmittedAllTerms = true;
                    this.$emit('audit-event', {
                        eventType: 'all_terms_acknowledged',
                        data: { termsCount: newVal.length }
                    });
                }
            },
            deep: true
        }
    },
    mounted() {
        this.$emit('audit-event', {
            eventType: 'agreement_loaded',
            data: {
                planId: this.plan.id,
                agreementTotal: this.plan.agreement_total,
                term: this.plan.term
            }
        });
    },
    methods: {
        onViewTerms() {
            this.$emit('audit-event', {
                eventType: 'terms_viewed',
                data: {}
            });
            this.$emit('show-terms');
        }
    }
};
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .what-youll-need {
        border-left: 4px solid #464577;
        background: #fafbff;

        h5 {
            color: #464577;
            font-weight: 600;
        }

        .what-youll-need-list {
            padding-left: 1.4rem;

            li {
                margin-bottom: 0.35rem;
            }
        }

        .no-credit-check {
            background: #e6f4ea;
            border-radius: 6px;
            padding: 10px 14px;
            color: #1c7430;
        }
    }

    .details-card {
        padding: 0 !important;

        .card-body {
            padding: 0 !important;
        }
    }

    .details-table {
        width: 100%;

        th {
            text-transform: uppercase;
            padding: 0.75rem 1.5rem;
            border-top: 1px solid $gray-300;
            font-size: 0.9rem;
            width: 30%;
            vertical-align: top;
            font-weight: 600;
            background-color: $gray-100;

            &.no-border {
                border-top: none;
            }
        }

        td {
            padding: 0.75rem 1.5rem;
            border-top: 1px solid $gray-300;
            vertical-align: top;
            width: 70%;
            font-size: 0.95rem;

            &.no-border {
                border-top: none;
            }
        }
    }

    .checkbox-list {
        .checkbox-item {
            padding: 15px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            margin-bottom: 15px;
            transition: all 0.2s ease;

            &:hover {
                border-color: #46457776;
                box-shadow: 0 2px 4px rgba(70, 69, 119, 0.1);
            }

            &:has(input:checked),
            &:has(.custom-control-input:checked) {
                border-color: #464577;
                box-shadow: 0 2px 4px rgba(70, 69, 119, 0.1);
            }

        }
    }
</style>
